function safeEscapeHtml(value) {
    return String(value ?? '').replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\"/g, '&quot;')
        .replace(/'/g, '&#039;');
}
if (typeof window !== 'undefined') {
    window.bootstrapAdminData = bootstrapAdminData;
}

function fallbackIsAdminLoggedIn() {
    const session = (typeof currentAdmin !== 'undefined' && currentAdmin) || (typeof window !== 'undefined' && window.currentAdmin)
        || null;
    if (session && session.uid) return true;
    return false;
}

function getGlobalArray(name) {
    const candidates = [];
    if (typeof window !== 'undefined') {
        candidates.push(window[name]);
        if (name === 'orders') candidates.push(window.__wtE_orders, window.clientMessages);
        if (name === 'clientMessages') candidates.push(window.__wtE_clientMessages, window.orders);
        if (name === 'reviews') candidates.push(window.__wtE_reviews);
        if (name === 'menuItems') candidates.push(window.__wtE_allMenuItems);
    }
    if (typeof globalThis !== 'undefined') {
        candidates.push(globalThis[name]);
    }
    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
    }
    return [];
}

if (typeof window !== 'undefined') {
    window.escapeHtml = window.escapeHtml || safeEscapeHtml;
    window.isAdminLoggedIn = window.isAdminLoggedIn || fallbackIsAdminLoggedIn;
}

function setAdminBootstrapError(message, details) {
    if (typeof window === 'undefined') return;
    const msg = String(message || 'Failed to load admin dashboard data.');
    const reason = details && details.message ? details.message : details;
    const fullMessage = reason ? `${msg} ${reason}` : msg;
    window.__adminBootstrapError = {
        message: fullMessage,
        updatedAt: new Date().toISOString()
    };
    if (typeof showNotification === 'function') {
        showNotification(fullMessage, 'error');
    }
}

function getAdminBootstrapRecoveryHint(errorLike) {
    const fallbackErrorText = errorLike && typeof errorLike === 'object' && 'error' in errorLike
        ? errorLike.error
        : '';
    const text = String(
        (errorLike && (errorLike.code || errorLike.message || fallbackErrorText)) || errorLike || ''
    ).toLowerCase();

    if (text.includes('permission-denied') || text.includes('insufficient permissions')) {
        return 'Firestore rejected the request. If this is localhost, verify deployed Firestore rules and App Check settings (register a debug token or disable enforcement for local development).';
    }
    if (text.includes('cache-only') || text.includes('could not reach firestore backend') || text.includes('appcheck/fetch-status-error')) {
        return 'Firestore is currently serving cache-only data because backend access is blocked. Register your App Check debug token in Firebase Console (or disable Firestore App Check enforcement for localhost).';
    }
    if (text.includes('app-check') || text.includes('appcheck')) {
        return 'App Check validation failed. Register your debug token in Firebase Console or configure a valid App Check site key.';
    }
    if (text.includes('network') || text.includes('unavailable')) {
        return 'Network access to Firestore failed. Check connectivity and retry.';
    }
    return 'Please sign out and sign back in, then retry.';
}

function clearAdminBootstrapError() {
    if (typeof window === 'undefined') return;
    window.__adminBootstrapError = null;
}

// ==================== BOOTSTRAP ADMIN DATA FROM FIRESTORE ====================
// Re-fetches clients and providers from Firestore and updates local arrays + localStorage.
// This ensures the admin dashboard shows fresh data after deletions or changes.
//
// Calls are coalesced: panel renders share one in-flight fetch and reuse a
// result completed within ADMIN_BOOTSTRAP_REUSE_MS. Pass { force: true } after
// mutations so the returned data comes from a fetch started after the call.
const ADMIN_BOOTSTRAP_REUSE_MS = 2000;
let adminBootstrapInFlight = null;
let adminBootstrapQueued = null;
let adminBootstrapLast = null;

function getAdminBootstrapSessionKey() {
    const auth = typeof window !== 'undefined' ? window.firebaseAuthInstance : null;
    return auth && auth.currentUser ? auth.currentUser.uid : null;
}

function startAdminBootstrapRun(options) {
    const sessionKey = getAdminBootstrapSessionKey();
    const run = runAdminBootstrap(options)
        .then((result) => {
            if (result && result.ok) {
                adminBootstrapLast = { at: Date.now(), sessionKey, result };
            } else {
                adminBootstrapLast = null;
            }
            return result;
        }, (error) => {
            adminBootstrapLast = null;
            throw error;
        })
        .finally(() => {
            if (adminBootstrapInFlight === run) adminBootstrapInFlight = null;
        });
    adminBootstrapInFlight = run;
    return run;
}

function bootstrapAdminData(options = {}) {
    const force = options.force === true;
    const runOptions = { ...options };
    delete runOptions.force;

    if (!force && !adminBootstrapInFlight && adminBootstrapLast &&
        Date.now() - adminBootstrapLast.at < ADMIN_BOOTSTRAP_REUSE_MS &&
        adminBootstrapLast.sessionKey === getAdminBootstrapSessionKey()) {
        return Promise.resolve(adminBootstrapLast.result);
    }

    if (!adminBootstrapInFlight) {
        return startAdminBootstrapRun(runOptions);
    }

    if (!force) {
        return adminBootstrapInFlight;
    }

    // A forced request during an in-flight fetch must not receive data read
    // before the change; schedule exactly one trailing fetch shared by all
    // forced callers that arrive meanwhile.
    if (!adminBootstrapQueued) {
        const settle = () => {};
        adminBootstrapQueued = adminBootstrapInFlight.then(settle, settle).then(() => {
            adminBootstrapQueued = null;
            return startAdminBootstrapRun(runOptions);
        });
    }
    return adminBootstrapQueued;
}

async function runAdminBootstrap(options = {}) {
    console.log('[Admin] Bootstrapping data from Firestore...');
    const fetchMeta = {
        clientsFromCache: false,
        providersFromCache: false,
        menuItemsFromCache: false,
        ordersFromCache: false,
        reviewsFromCache: false
    };
    const readErrors = [];

    const guard = typeof isAdminLoggedIn === 'function' ? isAdminLoggedIn : fallbackIsAdminLoggedIn;
    if (!guard()) {
        console.log('[Admin] Skipping Firestore bootstrap because no active admin session is present.');
        return { ok: false, skipped: true };
    }
    if (typeof window !== 'undefined' && typeof window.firebaseAuthInstance !== 'undefined' && window.firebaseAuthInstance && window.firebaseAuthInstance.currentUser && typeof isAdminUser === 'function') {
        try {
            const user = window.firebaseAuthInstance.currentUser;
            const isAdmin = await isAdminUser(user);
            if (!isAdmin) {
                console.warn('[Admin] Skipping Firestore bootstrap because the current Firebase user is not an admin.');
                setAdminBootstrapError('Admin access token is missing required claim. Please sign out and sign back in.');
                return { ok: false, error: 'Missing admin claim on current token.' };
            }
        } catch (e) {
            console.warn('[Admin] Admin guard check failed during bootstrap:', e.message || e);
            setAdminBootstrapError('Could not validate admin token before loading dashboard.', e);
            return { ok: false, error: e && e.message ? e.message : String(e) };
        }
    }
    // Ensure Firestore is initialized
    if (typeof window.ensureFirebaseReady === 'function') {
        try {
            await window.ensureFirebaseReady();
        } catch (e) {
            console.warn('[Admin] ensureFirebaseReady failed:', e);
            setAdminBootstrapError('Firebase failed to initialize for admin dashboard.', e);
            return { ok: false, error: e && e.message ? e.message : String(e) };
        }
    }

    const db = window.firebaseDbInstance;
    if (!db) {
        console.warn('[Admin] Firestore not available, cannot bootstrap data');
        setAdminBootstrapError('Firestore is not available for admin dashboard loading.');
        return { ok: false, error: 'Firestore instance unavailable.' };
    }

    try {
        // Fetch clients from Firestore
        const clientsSnapshot = await db.collection('clients').get();
        fetchMeta.clientsFromCache = clientsSnapshot && clientsSnapshot.fromCache === true;
        const freshClients = [];
        clientsSnapshot.forEach(doc => {
            const data = doc.data();
            if (data && data.email) {
                freshClients.push({ id: doc.id, ...data });
            }
        });

        // Deduplicate clients by email
        const uniqueClients = [];
        const seenEmails = new Set();
        for (const c of freshClients) {
            if (c.email && !seenEmails.has(c.email.toLowerCase())) {
                seenEmails.add(c.email.toLowerCase());
                uniqueClients.push(c);
            }
        }

        // PROTECT SUPER ADMIN: Exclude the backend-issued admin user from the
        // clients list so it can never be shown in "All Clients" tab and
        // accidentally deleted. Admin identity is determined by the backend
        // `admin: true` custom claim (no hardcoded admin email is used).
        const filteredClients = uniqueClients.filter(c => {
            return !(c.role === 'admin' || c.admin === true);
        });

        // Update global clients array
        window.clients = filteredClients;
        try {
        } catch (e) {
            console.warn('[Admin] Could not save clients to localStorage:', e);
        }
        console.log('[Admin] Bootstrapped clients:', filteredClients.length);

        // Fetch providers from Firestore
        const providersSnapshot = await db.collection('providers').get();
        fetchMeta.providersFromCache = providersSnapshot && providersSnapshot.fromCache === true;
        const freshProviders = [];
        providersSnapshot.forEach(doc => {
            const data = doc.data();
            if (data && data.email) {
                freshProviders.push({ id: doc.id, ...data });
            }
        });

        // Filter out permanently deleted providers
        const deletedEmails = (typeof getDeletedProviderEmails === 'function') ? getDeletedProviderEmails() : [];
        let filteredProviders = freshProviders.filter(p => {
            return !(p.email && deletedEmails.includes(p.email.toLowerCase()));
        });

        // PROTECT SUPER ADMIN: Also filter out the backend-issued admin user from
        // the providers list (no hardcoded admin email is used).
        filteredProviders = filteredProviders.filter(p => {
            return !(p.role === 'admin' || p.admin === true);
        });

        // Update global providers array
        window.providers = filteredProviders;
        try {
        } catch (e) {
            console.warn('[Admin] Could not save providers to localStorage:', e);
        }
        console.log('[Admin] Bootstrapped providers:', filteredProviders.length);

        // ============ CROSS-BROWSER SYNC FIX: Also fetch menuItems, orders,
        // clientMessages (legacy), and reviews from Firestore ============
        // Firestore is the SINGLE SOURCE OF TRUTH. The Platform Statistics grid
        // and admin panels read menuItems / clientMessages / reviews arrays, so we
        // must refresh ALL of them here (not just clients/providers) to guarantee
        // normal Chrome and Incognito render identical data regardless of whether
        // the onSnapshot listeners have attached yet.

        // Helper: normalize Firestore Timestamp objects to ISO strings for display.
        function normalizeFsTimestamp(value) {
            if (value && typeof value === 'object' && typeof value.toDate === 'function') {
                try { return value.toDate().toISOString(); } catch (e) { return value; }
            }
            return value;
        }
        function normalizeDocTimestamps(obj) {
            if (!obj || typeof obj !== 'object') return obj;
            const out = { ...obj };
            ['createdAt', 'updatedAt', 'servingsLeftUpdatedAt', 'subscriptionStart', 'subscriptionEnd', 'subscriptionApprovedAt'].forEach(k => {
                if (out[k] !== undefined) out[k] = normalizeFsTimestamp(out[k]);
            });
            if (Array.isArray(out.statusHistory)) {
                out.statusHistory = out.statusHistory.map(entry => {
                    if (entry && entry.timestamp !== undefined) {
                        return { ...entry, timestamp: normalizeFsTimestamp(entry.timestamp) };
                    }
                    return entry;
                });
            }
            return out;
        }

        // ---- menuItems ----
        try {
            const menuSnap = await db.collection('menuItems').get();
            fetchMeta.menuItemsFromCache = menuSnap && menuSnap.fromCache === true;
            const freshMenuItems = [];
            menuSnap.forEach(doc => {
                const data = normalizeDocTimestamps(doc.data() || {});
                if (data && data.id == null) data.id = doc.id;
                freshMenuItems.push(data);
            });
            window.menuItems = freshMenuItems;
            window.__wtE_allMenuItems = freshMenuItems;
            console.log('[Admin] Bootstrapped menuItems:', freshMenuItems.length);
        } catch (e) {
            console.warn('[Admin] Could not bootstrap menuItems:', e.message);
            readErrors.push({ collection: 'menuItems', message: e && e.message ? e.message : String(e) });
        }

        // ---- orders (primary) ----
        const freshOrdersMap = new Map();
        try {
            const ordersSnap = await db.collection('orders').get();
            fetchMeta.ordersFromCache = ordersSnap && ordersSnap.fromCache === true;
            ordersSnap.forEach(doc => {
                const data = normalizeDocTimestamps(doc.data() || {});
                if (data && data.id == null) data.id = doc.id;
                if (data && data.id != null) freshOrdersMap.set(String(data.id), data);
            });
            console.log('[Admin] Bootstrapped orders:', freshOrdersMap.size);
        } catch (e) {
            console.warn('[Admin] Could not bootstrap orders:', e.message);
            readErrors.push({ collection: 'orders', message: e && e.message ? e.message : String(e) });
        }

        // Note: clientMessages collection is deprecated; orders are loaded strictly from the orders collection above.

        const freshOrders = Array.from(freshOrdersMap.values())
            .sort((a, b) => {
                const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return tb - ta;
            });
        window.clientMessages = freshOrders;
        window.orders = freshOrders;
        window.__wtE_clientMessages = freshOrders;
        window.__wtE_orders = freshOrders;
        console.log('[Admin] Bootstrapped total orders/clientMessages:', freshOrders.length);

        // ---- reviews ----
        try {
            const reviewsSnap = await db.collection('reviews').get();
            fetchMeta.reviewsFromCache = reviewsSnap && reviewsSnap.fromCache === true;
            const freshReviews = [];
            reviewsSnap.forEach(doc => {
                const data = normalizeDocTimestamps(doc.data() || {});
                if (data && data.id == null) data.id = doc.id;
                freshReviews.push(data);
            });
            window.reviews = freshReviews;
            console.log('[Admin] Bootstrapped reviews:', freshReviews.length);
        } catch (e) {
            console.warn('[Admin] Could not bootstrap reviews:', e.message);
            readErrors.push({ collection: 'reviews', message: e && e.message ? e.message : String(e) });
        }
        // ============ END CROSS-BROWSER SYNC FIX ============
        if (readErrors.length > 0) {
            if (!guard()) {
                console.log('[Admin] Discarding bootstrap results because the admin session ended.');
                return { ok: false, skipped: true };
            }
            const joined = readErrors.map(err => `${err.collection}: ${err.message}`).join(' | ');
            const hasPermissionError = /permission|insufficient/i.test(joined);
            const currentUser = window.firebaseAuthInstance && window.firebaseAuthInstance.currentUser ? window.firebaseAuthInstance.currentUser : null;
            if (hasPermissionError && !options._retried && currentUser && typeof currentUser.getIdToken === 'function') {
                try {
                    await currentUser.getIdToken(true);
                    await new Promise((resolve) => setTimeout(resolve, 300));
                    return runAdminBootstrap({ ...options, _retried: true });
                } catch (refreshErr) {
                    console.warn('[Admin] Token refresh retry failed after read permission errors:', refreshErr && refreshErr.message ? refreshErr.message : refreshErr);
                }
            }
            setAdminBootstrapError('Admin dashboard data failed to load from Firestore.', joined);
            return { ok: false, error: joined };
        }

        const counts = {
            clients: Array.isArray(window.clients) ? window.clients.length : 0,
            providers: Array.isArray(window.providers) ? window.providers.length : 0,
            orders: Array.isArray(window.orders) ? window.orders.length : 0,
            menuItems: Array.isArray(window.menuItems) ? window.menuItems.length : 0,
            reviews: Array.isArray(window.reviews) ? window.reviews.length : 0
        };
        const fetchedFromCacheOnly = fetchMeta.clientsFromCache &&
            fetchMeta.providersFromCache &&
            fetchMeta.menuItemsFromCache &&
            fetchMeta.ordersFromCache &&
            fetchMeta.reviewsFromCache;
        const allZero = counts.clients === 0 &&
            counts.providers === 0 &&
            counts.orders === 0 &&
            counts.menuItems === 0 &&
            counts.reviews === 0;
        if (fetchedFromCacheOnly && allZero) {
            const offlineHint = 'Firestore returned cache-only empty results. This usually means Firestore backend access is blocked (often App Check enforcement/debug token setup on localhost).';
            setAdminBootstrapError('Admin dashboard data could not reach Firestore backend.', offlineHint);
            return { ok: false, error: offlineHint };
        }

        clearAdminBootstrapError();
        return { ok: true, counts };

    } catch (e) {
        if (!guard()) {
            console.log('[Admin] Discarding bootstrap error because the admin session ended.');
            return { ok: false, skipped: true };
        }
        console.error('[Admin] bootstrapAdminData error:', e);
        setAdminBootstrapError('Admin dashboard data failed to load from Firestore.', e);
        return { ok: false, error: e && e.message ? e.message : String(e) };
    }
}
if (typeof window !== 'undefined') {
    window.bootstrapAdminData = bootstrapAdminData;
}

// ==================== FORCE REFRESH ALL ADMIN PANELS ====================
// After deletion or data changes, call this to refresh ALL admin panels
// This ensures both the Firestore-backed panels AND the original app.js panels are updated
async function refreshAllAdminPanels() {
    console.log('[Admin] Refreshing all admin panels...');

    // Fetch fresh data once; the panel renders below reuse this result.
    await bootstrapAdminData({ force: true });

    // Re-render ALL admin panels (both new Firestore-backed and original app.js versions)
    try {
        // Firestore-backed panels (from renderAdminSubscriptions-extract.js)
        if (typeof renderAdminSubscriptions === 'function') {
            renderAdminSubscriptions();
        }
        if (typeof renderAdminClients === 'function') {
            renderAdminClients();
        }
        if (typeof renderAdminProviders === 'function') {
            renderAdminProviders();
        }
        if (typeof renderAdminOrders === 'function') {
            renderAdminOrders();
        }
        if (typeof renderAdminReviews === 'function') {
            renderAdminReviews();
        }
        if (typeof renderAdminStats === 'function') {
            renderAdminStats();
        }
    } catch (e) {
        console.warn('[Admin] Error refreshing Firestore-backed panels:', e);
    }

    // Also try to refresh the original app.js panels (they use different container IDs)
    try {
        // These are the original app.js render functions that write to different containers
        if (typeof window.renderAdminStats === 'function') {
            window.renderAdminStats();
        }
        if (typeof window.renderAdminClients === 'function') {
            window.renderAdminClients();
        }
        if (typeof window.renderAdminProviders === 'function') {
            window.renderAdminProviders();
        }
    } catch (e) {
        console.warn('[Admin] Error refreshing app.js panels:', e);
    }

    console.log('[Admin] All admin panels refreshed');
}
if (typeof window !== 'undefined') {
    window.refreshAllAdminPanels = refreshAllAdminPanels;
}

async function renderAdminSubscriptions() {
    console.log('[Admin] Rendering subscriptions (Firestore-backed)...');
    const container = document.getElementById('adminProviderSubscriptionsList') || document.getElementById('admin-subscriptions-panel');
    if (!container) return;

    // Always bootstrap fresh data from Firestore first to ensure latest state
    await bootstrapAdminData();

    const providerSubs = getGlobalArray('providers');

    let html = '<div class="data-list"><h3>Provider Subscriptions</h3>';

    if (providerSubs.length === 0) {
        html += `
            <div class="empty-state">
                <p>No providers found.</p>
            </div>
        `;
        html += '</div>';
        container.innerHTML = html;
        return;
    }

    providerSubs.forEach(provider => {
        const providerId = provider.id || provider.uid || '';
        const subStatus = provider.subscriptionStatus || 'pending';
        const subPlan = provider.subscriptionPlan || 'free';
        const subEnd = provider.subscriptionEnd ? new Date(provider.subscriptionEnd).toLocaleDateString() : 'N/A';

        let statusBadge = '';
        if (subStatus === 'pending') statusBadge = '<span style="color:#f39c12">⏳ Pending</span>';
        else if (subStatus === 'approved') statusBadge = '<span style="color:#27ae60">✅ Active</span>';
        else if (subStatus === 'rejected') statusBadge = '<span style="color:#e74c3c">❌ Rejected</span>';
        else if (subStatus === 'suspended') statusBadge = '<span style="color:#e67e22">⛔ Suspended</span>';

        // ============ STRIPE PAYMENT STATUS ============
        // Determine the Stripe payment status badge for this provider.
        // - Free plan: no payment required (shows N/A)
        // - Paid plan (basic/premium): shows Paid/Unpaid based on Stripe fields
        let stripePaymentBadge = '';
        if (subPlan === 'free') {
            stripePaymentBadge = '<span style="color:#95a5a6">💳 N/A (Free)</span>';
        } else if (provider.isStripePaid === true || provider.stripePaymentStatus === 'paid') {
            stripePaymentBadge = '<span style="color:#27ae60">💳 Paid</span>';
        } else if (provider.stripePaymentStatus === 'pending') {
            stripePaymentBadge = '<span style="color:#f39c12">💳 Payment Pending</span>';
        } else if (provider.stripePaymentStatus === 'failed') {
            stripePaymentBadge = '<span style="color:#e74c3c">💳 Payment Failed</span>';
        } else if (provider.stripeSubscriptionId) {
            stripePaymentBadge = '<span style="color:#27ae60">💳 Subscribed</span>';
        } else {
            stripePaymentBadge = '<span style="color:#e74c3c">💳 Unpaid</span>';
        }

        // Include Stripe subscription/customer IDs if available (for admin reference)
        let stripeDetails = '';
        if (provider.stripeCustomerId || provider.stripeSubscriptionId) {
            stripeDetails = `
                <span style="color:#7f8c8d; font-size:12px;">
                    ${provider.stripeCustomerId ? `Customer: ${escapeHtml(provider.stripeCustomerId)}` : ''}
                    ${provider.stripeSubscriptionId ? ` | Sub: ${escapeHtml(provider.stripeSubscriptionId)}` : ''}
                </span>
            `;
        }

        // Build action buttons based on subscription status
        let actionButtons = '';
        if (subStatus === 'pending') {
            actionButtons = `
                <button class="edit-btn" onclick="approveProvider('${providerId}')">Approve</button>
                <button class="delete-btn" onclick="rejectProvider('${providerId}')">Reject</button>
            `;
        } else if (subStatus === 'approved') {
            actionButtons = `
                <button class="edit-btn" onclick="renewProvider('${providerId}')">Renew</button>
                <button class="delete-btn" onclick="suspendProvider('${providerId}')" style="background:#e67e22;">Suspend</button>
                <button class="delete-btn" onclick="deleteAdminProvider('${providerId}')">Delete</button>
            `;
        } else if (subStatus === 'rejected') {
            actionButtons = `
                <button class="edit-btn" onclick="approveProvider('${providerId}')">Approve</button>
                <button class="delete-btn" onclick="deleteAdminProvider('${providerId}')">Delete</button>
            `;
        } else if (subStatus === 'suspended') {
            actionButtons = `
                <button class="edit-btn" onclick="activateProvider('${providerId}')">Activate</button>
                <button class="delete-btn" onclick="deleteAdminProvider('${providerId}')">Delete</button>
            `;
        }

        html += `
            <div class="data-item">
                <span><strong>${escapeHtml(provider.name || 'Unknown')}</strong></span>
                <span>${escapeHtml(provider.email || 'No email')}</span>
                <span>Plan: ${subPlan}</span>
                <span>Status: ${statusBadge}</span>
                <span>Payment: ${stripePaymentBadge}</span>
                <span>Expires: ${subEnd}</span>
                ${stripeDetails}
                <div style="margin-top: 8px; display: flex; gap: 6px; flex-wrap: wrap;">
                    ${actionButtons}
                </div>
            </div>
        `;
    });

    html += '</div>';
    container.innerHTML = html;
}

async function renderAdminClients() {
    console.log('[Admin] Rendering clients (Firestore-backed)...');
    const container = document.getElementById('admin-clients-panel');
    if (!container) return;

    // Always bootstrap fresh data from Firestore first to ensure latest state
    await bootstrapAdminData();

    const clientsToRender = getGlobalArray('clients').filter(c => c && (c.subscriptionStatus !== undefined || c.email));

    let html = '<div class="data-list"><h3>All Clients</h3>';

    if (clientsToRender.length === 0) {
        html += `
            <div class="empty-state">
                <p>No clients found.</p>
            </div>
        `;
        html += '</div>';
        container.innerHTML = html;
        return;
    }

    clientsToRender
        .slice()
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .forEach(client => {
            html += `
                <div class="data-item">
                    <span>${escapeHtml(client.name || 'Unknown')}</span>
                    <span>${escapeHtml(client.email || 'No email')}</span>
                    <span class="admin-item-actions">
                        <button class="delete-btn" onclick="deleteAdminClient('${client.email || client.uid || client.id}')">Delete</button>
                    </span>
                </div>
            `;
        });

    html += '</div>';
    container.innerHTML = html;
}

async function renderAdminProviders() {
    console.log('[Admin] Rendering providers (Firestore-backed)...');
    const container = document.getElementById('admin-providers-panel');
    if (!container) return;

    // Always bootstrap fresh data from Firestore first to ensure latest state
    await bootstrapAdminData();

    const providersList = getGlobalArray('providers');

    let html = '<div class="data-list"><h3>All Providers</h3>';

    if (providersList.length === 0) {
        html += `
            <div class="empty-state">
                <p>No providers found.</p>
            </div>
        `;
        html += '</div>';
        container.innerHTML = html;
        return;
    }

    providersList.forEach(provider => {
        const providerId = provider.id || provider.uid || '';
        const subStatus = provider.subscriptionStatus || 'pending';
        let statusBadge = '';
        if (subStatus === 'pending') statusBadge = '<span style="color:#f39c12">⏳ Pending</span>';
        else if (subStatus === 'approved') statusBadge = '<span style="color:#27ae60">✅ Active</span>';
        else if (subStatus === 'rejected') statusBadge = '<span style="color:#e74c3c">❌ Rejected</span>';
        else if (subStatus === 'suspended') statusBadge = '<span style="color:#e67e22">⛔ Suspended</span>';

        // Build action buttons based on subscription status
        let actionButtons = '';
        if (subStatus === 'pending') {
            actionButtons = `
                <button class="edit-btn" onclick="approveProvider('${providerId}')">Approve</button>
                <button class="delete-btn" onclick="rejectProvider('${providerId}')">Reject</button>
            `;
        } else if (subStatus === 'approved') {
            actionButtons = `
                <button class="delete-btn" onclick="suspendProvider('${providerId}')" style="background:#e67e22;">Suspend</button>
                <button class="delete-btn" onclick="deleteAdminProvider('${providerId}')">Delete</button>
            `;
        } else if (subStatus === 'rejected') {
            actionButtons = `
                <button class="edit-btn" onclick="approveProvider('${providerId}')">Approve</button>
                <button class="delete-btn" onclick="deleteAdminProvider('${providerId}')">Delete</button>
            `;
        } else if (subStatus === 'suspended') {
            actionButtons = `
                <button class="edit-btn" onclick="activateProvider('${providerId}')">Activate</button>
                <button class="delete-btn" onclick="deleteAdminProvider('${providerId}')">Delete</button>
            `;
        }

        html += `
            <div class="data-item">
                <span><strong>${escapeHtml(provider.name || 'Unknown')}</strong></span>
                <span>${escapeHtml(provider.email || 'No email')}</span>
                <span>${escapeHtml(provider.type || 'N/A')}</span>
                <span>${statusBadge}</span>
                <div style="margin-top: 8px; display: flex; gap: 6px; flex-wrap: wrap;">
                    ${actionButtons}
                </div>
            </div>
        `;
    });

    html += '</div>';
    container.innerHTML = html;
}

async function renderAdminOrders() {
    console.log('[Admin] Rendering orders (Firestore-backed)...');
    const container = document.getElementById('admin-orders-panel');
    if (!container) return;

    const orders = getGlobalArray('orders').length ? getGlobalArray('orders') : getGlobalArray('clientMessages');

    let html = '<div class="data-list"><h3>All Orders</h3>';

    if (orders.length === 0) {
        html += `
            <div class="empty-state">
                <p>No orders found.</p>
            </div>
        `;
        html += '</div>';
        container.innerHTML = html;
        return;
    }

    orders.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    orders.forEach(order => {
        const statusInfo = getStatusInfo ? getStatusInfo(order.status || 'pending') : { text: order.status || 'pending' };
        html += `
            <div class="data-item">
                <span><strong>${escapeHtml(order.foodItem || 'N/A')}</strong></span>
                <span>Client: ${escapeHtml(order.clientName || 'N/A')}</span>
                <span>Provider: ${escapeHtml(order.providerName || 'N/A')}</span>
                <span>Status: ${escapeHtml(statusInfo.text)}</span>
                <span>$${(order.price || 0).toFixed(2)}</span>
            </div>
        `;
    });

    html += '</div>';
    container.innerHTML = html;
}

async function renderAdminReviews() {
    console.log('[Admin] Rendering reviews (Firestore-backed)...');
    const container = document.getElementById('admin-reviews-panel');
    if (!container) return;

    const reviewsList = getGlobalArray('reviews');

    let html = '<div class="data-list"><h3>All Reviews</h3>';

    if (reviewsList.length === 0) {
        html += `
            <div class="empty-state">
                <p>No reviews found.</p>
            </div>
        `;
        html += '</div>';
        container.innerHTML = html;
        return;
    }

    reviewsList.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

    reviewsList.forEach(review => {
        html += `
            <div class="data-item">
                <span><strong>${escapeHtml(review.providerName || 'N/A')}</strong></span>
                <span>Rating: ${'★'.repeat(review.rating || 0)}${'☆'.repeat(5 - (review.rating || 0))}</span>
                <span>By: ${escapeHtml(review.clientName || review.clientEmail || 'Anonymous')}</span>
                ${review.comment ? `<span>"${escapeHtml(review.comment)}"</span>` : ''}
            </div>
        `;
    });

    html += '</div>';
    container.innerHTML = html;
}

async function renderAdminSettings() {
    console.log('[Admin] Rendering settings...');
    const container = document.getElementById('admin-settings-panel');
    if (!container) return;

    // Always load settings fresh from Firestore (no localStorage) so the
    // admin panel reflects the latest saved values even if the initial
    // page-load fetch ran before the user was authenticated.
    try {
        if (typeof __loadSiteSettingsFromFirestore === 'function') {
            await __loadSiteSettingsFromFirestore();
        }
        if (typeof __loadSubscriptionRatesFromFirestore === 'function') {
            await __loadSubscriptionRatesFromFirestore();
        }
    } catch (e) {
        console.warn('[Admin] Error loading settings from Firestore:', e);
    }

    const settings = loadSiteSettings();
    const rates = loadSubscriptionRates();
    const renewalConfig = loadPlanRenewalConfig();
    const countryAccess = loadCountryAccessSettings();
    const countryAccessListValue = escapeHtml((countryAccess.countries || []).join(', '));

    container.innerHTML = `
        <div class="settings-section">
            <h3>Admin Settings</h3>

            <!-- Footer Text Settings -->
            <div class="settings-group" style="margin-bottom: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px;">
                <h4 style="margin-top: 0;">Footer Text</h4>
                <label for="footerTextInput" style="display: block; margin-bottom: 8px; font-weight: bold;">Footer Text:</label>
                <textarea id="footerTextInput" rows="2" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 10px;">${escapeHtml(settings.footerText)}</textarea>
                <div style="margin-bottom: 10px;">
                    <strong>Preview:</strong>
                    <div id="footerPreview" style="background: #fff; border: 1px solid #ddd; border-radius: 4px; padding: 10px; margin-top: 5px; color: #666; font-size: 14px;">${escapeHtml(settings.footerText)}</div>
                </div>
                <div style="display: flex; gap: 10px;">
                    <button class="edit-btn" onclick="saveFooterText()">Save Footer Text</button>
                    <button class="delete-btn" onclick="resetFooterText()">Reset to Default</button>
                </div>
            </div>

            <!-- Recurring Subscription Rates -->
            <div class="settings-group" style="margin-bottom: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px;">
                <h4 style="margin-top: 0;">Recurring Subscription Rates (Provider Plans)</h4>
                <p style="color: #666; font-size: 13px; margin-bottom: 15px;">Set the monthly recurring rates for provider subscription plans. These rates are used for Stripe billing and shown to providers during registration.</p>

                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
                    <div>
                        <label for="basicPlanRate" style="display: block; margin-bottom: 8px; font-weight: bold;">Basic Plan Rate ($/month):</label>
                        <input type="number" id="basicPlanRate" min="0" step="0.01" value="${rates.basic.toFixed(2)}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px;">
                    </div>
                    <div>
                        <label for="premiumPlanRate" style="display: block; margin-bottom: 8px; font-weight: bold;">Premium Plan Rate ($/month):</label>
                        <input type="number" id="premiumPlanRate" min="0" step="0.01" value="${rates.premium.toFixed(2)}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px;">
                    </div>
                </div>

                <div id="subscriptionRatesStatus" style="margin-bottom: 10px; font-size: 13px; color: #27ae60;"></div>

                <div style="display: flex; gap: 10px;">
                    <button class="approve-btn" onclick="saveSubscriptionRates()">Save Subscription Rates</button>
                    <button class="delete-btn" onclick="resetSubscriptionRates()">Reset to Defaults</button>
                </div>
            </div>

            <!-- Monthly Renewal Configuration -->
            <div class="settings-group" style="padding: 20px; background: #f8f9fa; border-radius: 8px;">
                <h4 style="margin-top: 0;">Monthly Renewal Configuration</h4>
                <p style="color: #666; font-size: 13px; margin-bottom: 15px;">Configure how each provider subscription plan renews. Plans set to "Monthly Renewal" will renew every 30 days (recurring billing cycle).</p>

                <div style="display: grid; grid-template-columns: 1fr; gap: 15px; margin-bottom: 15px;">
                    <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px; background: #fff; border: 1px solid #ddd; border-radius: 6px;">
                        <div>
                            <strong>Free Plan</strong>
                            <div style="color: #666; font-size: 12px;">Basic access - no billing</div>
                        </div>
                        <label style="display: flex; align-items: center; gap: 8px; font-weight: bold;">
                            <input type="checkbox" id="freeRenewsMonthly" ${renewalConfig.free ? 'checked' : ''} style="width: 18px; height: 18px;">
                            Monthly Renewal
                        </label>
                    </div>
                    <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px; background: #fff; border: 1px solid #ddd; border-radius: 6px;">
                        <div>
                            <strong>Basic Plan</strong>
                            <div style="color: #666; font-size: 12px;">$${rates.basic.toFixed(2)}/month - full menu, unlimited orders, analytics</div>
                        </div>
                        <label style="display: flex; align-items: center; gap: 8px; font-weight: bold;">
                            <input type="checkbox" id="basicRenewsMonthly" ${renewalConfig.basic ? 'checked' : ''} style="width: 18px; height: 18px;">
                            Monthly Renewal
                        </label>
                    </div>
                    <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px; background: #fff; border: 1px solid #ddd; border-radius: 6px;">
                        <div>
                            <strong>Premium Plan</strong>
                            <div style="color: #666; font-size: 12px;">$${rates.premium.toFixed(2)}/month - unlimited menu, priority support, custom branding</div>
                        </div>
                        <label style="display: flex; align-items: center; gap: 8px; font-weight: bold;">
                            <input type="checkbox" id="premiumRenewsMonthly" ${renewalConfig.premium ? 'checked' : ''} style="width: 18px; height: 18px;">
                            Monthly Renewal
                        </label>
                    </div>
                </div>

                <div id="planRenewalConfigStatus" style="margin-bottom: 10px; font-size: 13px; color: #27ae60;"></div>

                <div style="display: flex; gap: 10px;">
                    <button class="approve-btn" onclick="savePlanRenewalConfig()">Save Renewal Configuration</button>
                    <button class="delete-btn" onclick="resetPlanRenewalConfig()">Reset to Defaults</button>
                </div>
            </div>

            <!-- Country / Region Access Restrictions -->
            <div class="settings-group" style="margin-top: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px;">
                <h4 style="margin-top: 0;">Country / Region Access Restrictions</h4>
                <p style="color: #666; font-size: 13px; margin-bottom: 15px;">
                    By default every country in the world is enabled for client and provider registration/ordering.
                    Use this to restrict launch to specific countries (allowlist) or to block specific countries
                    (blocklist) without touching code. Changes take effect immediately for new registrations,
                    logins, menu edits, and provider profile updates across all browsers.
                </p>

                <label for="countryAccessMode" style="display: block; margin-bottom: 8px; font-weight: bold;">Restriction Mode:</label>
                <select id="countryAccessMode" onchange="toggleCountryAccessListVisibility()" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 15px;">
                    <option value="all" ${countryAccess.mode === 'all' ? 'selected' : ''}>No restriction — every country enabled (default)</option>
                    <option value="allowlist" ${countryAccess.mode === 'allowlist' ? 'selected' : ''}>Allowlist — only the listed countries are enabled</option>
                    <option value="blocklist" ${countryAccess.mode === 'blocklist' ? 'selected' : ''}>Blocklist — every country enabled EXCEPT the listed ones</option>
                </select>

                <div id="countryAccessListGroup" style="display: ${countryAccess.mode === 'all' ? 'none' : 'block'}; margin-bottom: 15px;">
                    <label for="countryAccessList" style="display: block; margin-bottom: 8px; font-weight: bold;">Country Codes (comma-separated ISO 2-letter codes, e.g. US, CA, GB):</label>
                    <textarea id="countryAccessList" rows="2" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px;">${countryAccessListValue}</textarea>
                </div>

                <label for="countryAccessReason" style="display: block; margin-bottom: 8px; font-weight: bold;">Message shown to blocked users (optional):</label>
                <input type="text" id="countryAccessReason" value="${escapeHtml(countryAccess.reason || '')}" placeholder="This market is not yet enabled for launch." style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 10px;">

                <div id="countryAccessStatus" style="margin-bottom: 10px; font-size: 13px; color: #27ae60;"></div>

                <div style="display: flex; gap: 10px;">
                    <button class="approve-btn" onclick="saveCountryAccessSettings()">Save Country Access Settings</button>
                    <button class="delete-btn" onclick="resetCountryAccessSettings()">Remove All Restrictions</button>
                </div>
            </div>
        </div>
    `;

    // Update footer preview in real-time when user types
    const footerTextInput = document.getElementById('footerTextInput');
    const footerPreview = document.getElementById('footerPreview');
    if (footerTextInput && footerPreview) {
        footerTextInput.addEventListener('input', function() {
            footerPreview.textContent = this.value;
        });
    }
}

// ==================== FIRESTORE-BACKED SETTINGS (NO localStorage) ====================
// These functions OVERRIDE the app.js versions (which used localStorage).
// Settings are persisted to the Firestore 'siteSettings' collection (doc id: 'main')
// so changes survive page refresh and sync across all browsers.
// Caches are populated from Firestore on page load and when the Settings tab opens.

// In-memory caches (populated from Firestore)
let __siteSettingsCache = null;
let __subscriptionRatesCache = null;
let __siteSettingsReadBlocked = false;

// Defaults (mirror app.js defaults)
const __DEFAULT_SITE_SETTINGS = {
    footerText: '© 2026 What To Eat? - Connecting hungry people with available meals'
};
const __DEFAULT_SUBSCRIPTION_RATES = {
    free: 0.00,
    basic: 100.50,
    premium: 200.00
};

// Country/region access restriction defaults: 'all' = global launch (no
// restriction), 'allowlist' = only listed countries can register/order,
// 'blocklist' = all countries EXCEPT listed ones can register/order.
const __DEFAULT_COUNTRY_ACCESS_SETTINGS = {
    mode: 'all',
    countries: [],
    reason: ''
};

// Load site settings from Firestore into the cache
async function __loadSiteSettingsFromFirestore() {
    if (__siteSettingsReadBlocked) {
        __siteSettingsCache = __siteSettingsCache || { ...__DEFAULT_SITE_SETTINGS };
        return __siteSettingsCache;
    }
    const db = window.firebaseDbInstance;
    if (!db) {
        console.warn('[Settings] Firestore not available, using defaults');
        __siteSettingsCache = { ...__DEFAULT_SITE_SETTINGS };
        return __siteSettingsCache;
    }
    try {
        const doc = await db.collection('siteSettings').doc('main').get();
        if (doc.exists) {
            const data = doc.data() || {};
            __siteSettingsCache = {
                footerText: data.footerText || __DEFAULT_SITE_SETTINGS.footerText
            };
            window.__countryAccessSettingsCache = {
                mode: data.countryAccessMode || __DEFAULT_COUNTRY_ACCESS_SETTINGS.mode,
                countries: Array.isArray(data.countryAccessList) ? data.countryAccessList : __DEFAULT_COUNTRY_ACCESS_SETTINGS.countries,
                reason: typeof data.countryAccessReason === 'string' ? data.countryAccessReason : __DEFAULT_COUNTRY_ACCESS_SETTINGS.reason
            };
        } else {
            __siteSettingsCache = { ...__DEFAULT_SITE_SETTINGS };
            window.__countryAccessSettingsCache = { ...__DEFAULT_COUNTRY_ACCESS_SETTINGS };
        }
    } catch (e) {
        console.warn('[Settings] Error loading site settings from Firestore:', e.message);
        if (String(e && (e.code || e.message || e)).toLowerCase().includes('permission')) {
            __siteSettingsReadBlocked = true;
            console.warn('[Settings] Disabling repeated siteSettings reads after permission denial until next page reload.');
        }
        __siteSettingsCache = { ...__DEFAULT_SITE_SETTINGS };
        window.__countryAccessSettingsCache = window.__countryAccessSettingsCache || { ...__DEFAULT_COUNTRY_ACCESS_SETTINGS };
    }
    return __siteSettingsCache;
}

// Load subscription rates from Firestore into the cache
async function __loadSubscriptionRatesFromFirestore() {
    if (__siteSettingsReadBlocked) {
        __subscriptionRatesCache = __subscriptionRatesCache || { ...__DEFAULT_SUBSCRIPTION_RATES };
        return __subscriptionRatesCache;
    }
    const db = window.firebaseDbInstance;
    if (!db) {
        console.warn('[Settings] Firestore not available, using default rates');
        __subscriptionRatesCache = { ...__DEFAULT_SUBSCRIPTION_RATES };
        return __subscriptionRatesCache;
    }
    try {
        const doc = await db.collection('siteSettings').doc('main').get();
        if (doc.exists) {
            const data = doc.data() || {};
            __subscriptionRatesCache = {
                free: 0.00,
                basic: (typeof data.basicRate === 'number') ? data.basicRate : __DEFAULT_SUBSCRIPTION_RATES.basic,
                premium: (typeof data.premiumRate === 'number') ? data.premiumRate : __DEFAULT_SUBSCRIPTION_RATES.premium
            };
        } else {
            __subscriptionRatesCache = { ...__DEFAULT_SUBSCRIPTION_RATES };
        }
    } catch (e) {
        console.warn('[Settings] Error loading subscription rates from Firestore:', e.message);
        if (String(e && (e.code || e.message || e)).toLowerCase().includes('permission')) {
            __siteSettingsReadBlocked = true;
            console.warn('[Settings] Disabling repeated subscription rate reads after permission denial until next page reload.');
        }
        __subscriptionRatesCache = { ...__DEFAULT_SUBSCRIPTION_RATES };
    }
    return __subscriptionRatesCache;
}

// Save both settings + rates to Firestore in one doc write
async function __saveSettingsToFirestore(settings, rates) {
    const db = window.firebaseDbInstance;
    if (!db) {
        console.warn('[Settings] Firestore not available, cannot persist settings');
        return false;
    }
    try {
        await db.collection('siteSettings').doc('main').set({
            footerText: settings.footerText,
            basicRate: rates.basic,
            premiumRate: rates.premium,
            updatedAt: new Date().toISOString()
        }, { merge: true });
        console.log('[Settings] Saved settings to Firestore');
        return true;
    } catch (e) {
        console.error('[Settings] Error saving settings to Firestore:', e.message);
        return false;
    }
}

// OVERRIDE app.js loadSiteSettings() - returns from Firestore cache (no localStorage)
function loadSiteSettings() {
    if (__siteSettingsCache) {
        return { ...__siteSettingsCache };
    }
    return { ...__DEFAULT_SITE_SETTINGS };
}

// OVERRIDE app.js loadSubscriptionRates() - returns from Firestore cache (no localStorage)
function loadSubscriptionRates() {
    if (__subscriptionRatesCache) {
        return { ...__subscriptionRatesCache };
    }
    return { ...__DEFAULT_SUBSCRIPTION_RATES };
}

// Country/region access restriction settings (Super Admin-configurable).
// Returns from the shared window cache populated by __loadSiteSettingsFromFirestore().
function loadCountryAccessSettings() {
    if (window.__countryAccessSettingsCache) {
        return { ...window.__countryAccessSettingsCache, countries: [...(window.__countryAccessSettingsCache.countries || [])] };
    }
    return { ...__DEFAULT_COUNTRY_ACCESS_SETTINGS, countries: [] };
}

// Save the country access restriction settings to Firestore (siteSettings/main).
async function saveCountryAccessSettings() {
    if (!isAdminLoggedIn()) {
        showNotification('You must be logged in as Super Admin.', 'error');
        return;
    }
    const modeEl = document.getElementById('countryAccessMode');
    const listEl = document.getElementById('countryAccessList');
    const reasonEl = document.getElementById('countryAccessReason');
    if (!modeEl) return;

    const mode = modeEl.value === 'allowlist' || modeEl.value === 'blocklist' ? modeEl.value : 'all';
    const countries = (listEl ? listEl.value : '')
        .split(',')
        .map((c) => c.trim().toUpperCase())
        .filter((c) => /^[A-Z]{2}$/.test(c));
    const reason = reasonEl ? reasonEl.value.trim() : '';

    if (mode !== 'all' && countries.length === 0) {
        showNotification('Enter at least one 2-letter country code (comma-separated), or choose "No restriction".', 'error');
        return;
    }

    const db = window.firebaseDbInstance;
    if (!db) {
        showNotification('Firestore not available. Cannot save country access settings.', 'error');
        return;
    }

    try {
        await db.collection('siteSettings').doc('main').set({
            countryAccessMode: mode,
            countryAccessList: countries,
            countryAccessReason: reason,
            updatedAt: new Date().toISOString()
        }, { merge: true });

        window.__countryAccessSettingsCache = { mode, countries, reason };

        const statusEl = document.getElementById('countryAccessStatus');
        if (statusEl) {
            statusEl.textContent = '✅ Country access settings saved to Firestore!';
            statusEl.style.color = '#27ae60';
        }
        showNotification('Country/region access settings saved!', 'success');
    } catch (e) {
        console.error('[Settings] Error saving country access settings to Firestore:', e.message);
        const statusEl = document.getElementById('countryAccessStatus');
        if (statusEl) {
            statusEl.textContent = '❌ Failed to save country access settings. Check Firestore connection.';
            statusEl.style.color = '#e74c3c';
        }
        showNotification('Failed to save country access settings.', 'error');
    }
}

// Reset the country access restriction settings to "no restriction" (global launch).
async function resetCountryAccessSettings() {
    if (!isAdminLoggedIn()) {
        showNotification('You must be logged in as Super Admin.', 'error');
        return;
    }
    if (!confirm('Are you sure you want to remove all country/region restrictions? Every country will be enabled.')) return;

    const db = window.firebaseDbInstance;
    if (!db) {
        showNotification('Firestore not available. Cannot reset country access settings.', 'error');
        return;
    }

    try {
        await db.collection('siteSettings').doc('main').set({
            countryAccessMode: __DEFAULT_COUNTRY_ACCESS_SETTINGS.mode,
            countryAccessList: __DEFAULT_COUNTRY_ACCESS_SETTINGS.countries,
            countryAccessReason: __DEFAULT_COUNTRY_ACCESS_SETTINGS.reason,
            updatedAt: new Date().toISOString()
        }, { merge: true });

        window.__countryAccessSettingsCache = { ...__DEFAULT_COUNTRY_ACCESS_SETTINGS, countries: [] };

        const modeEl = document.getElementById('countryAccessMode');
        const listEl = document.getElementById('countryAccessList');
        const reasonEl = document.getElementById('countryAccessReason');
        if (modeEl) modeEl.value = 'all';
        if (listEl) listEl.value = '';
        if (reasonEl) reasonEl.value = '';
        if (typeof toggleCountryAccessListVisibility === 'function') toggleCountryAccessListVisibility();

        const statusEl = document.getElementById('countryAccessStatus');
        if (statusEl) {
            statusEl.textContent = '✅ Country access restrictions removed (global launch).';
            statusEl.style.color = '#27ae60';
        }
        showNotification('Country/region access restrictions removed.', 'success');
    } catch (e) {
        console.error('[Settings] Error resetting country access settings in Firestore:', e.message);
        showNotification('Failed to reset country access settings.', 'error');
    }
}

// Show/hide the country list textarea depending on the selected mode.
function toggleCountryAccessListVisibility() {
    const modeEl = document.getElementById('countryAccessMode');
    const listGroup = document.getElementById('countryAccessListGroup');
    if (!modeEl || !listGroup) return;
    listGroup.style.display = modeEl.value === 'all' ? 'none' : 'block';
}

// OVERRIDE app.js saveFooterText() - persists to Firestore (no localStorage)
async function saveFooterText() {
    if (!isAdminLoggedIn()) {
        showNotification('You must be logged in as Super Admin.', 'error');
        return;
    }
    const footerTextInput = document.getElementById('footerTextInput');
    if (!footerTextInput) return;
    const newFooterText = footerTextInput.value.trim();
    if (!newFooterText) {
        showNotification('Footer text cannot be empty.', 'error');
        return;
    }
    const settings = loadSiteSettings();
    settings.footerText = newFooterText;
    const rates = loadSubscriptionRates();
    const saved = await __saveSettingsToFirestore(settings, rates);
    if (saved) {
        __siteSettingsCache = { ...settings };
        showNotification('Footer text saved to Firestore!', 'success');
        if (typeof renderFooter === 'function') renderFooter();
    } else {
        showNotification('Failed to save footer text. Check Firestore connection.', 'error');
    }
}

// OVERRIDE app.js resetFooterText() - resets to default in Firestore (no localStorage)
async function resetFooterText() {
    if (!isAdminLoggedIn()) {
        showNotification('You must be logged in as Super Admin.', 'error');
        return;
    }
    if (!confirm('Are you sure you want to reset the footer text to default?')) return;
    const settings = loadSiteSettings();
    settings.footerText = __DEFAULT_SITE_SETTINGS.footerText;
    const rates = loadSubscriptionRates();
    const saved = await __saveSettingsToFirestore(settings, rates);
    if (saved) {
        __siteSettingsCache = { ...settings };
        const footerTextInput = document.getElementById('footerTextInput');
        const footerPreview = document.getElementById('footerPreview');
        if (footerTextInput) footerTextInput.value = __DEFAULT_SITE_SETTINGS.footerText;
        if (footerPreview) footerPreview.textContent = __DEFAULT_SITE_SETTINGS.footerText;
        showNotification('Footer text reset to default.', 'success');
        if (typeof renderFooter === 'function') renderFooter();
    } else {
        showNotification('Failed to reset footer text. Check Firestore connection.', 'error');
    }
}

// OVERRIDE app.js saveSubscriptionRates() - persists to Firestore (no localStorage)
async function saveSubscriptionRates() {
    if (!isAdminLoggedIn()) {
        showNotification('You must be logged in as Super Admin.', 'error');
        return;
    }
    const basicInput = document.getElementById('basicPlanRate');
    const premiumInput = document.getElementById('premiumPlanRate');
    if (!basicInput || !premiumInput) return;
    const basic = parseFloat(basicInput.value);
    const premium = parseFloat(premiumInput.value);
    if (isNaN(basic) || basic < 0 || isNaN(premium) || premium < 0) {
        showNotification('Please enter valid rates (0 or greater).', 'error');
        return;
    }
    const settings = loadSiteSettings();
    const rates = { free: 0.00, basic: basic, premium: premium };
    const saved = await __saveSettingsToFirestore(settings, rates);
    if (saved) {
        __subscriptionRatesCache = { ...rates };
        // Update Stripe config so provider registration shows the new rates
        if (window.stripeConfig && window.stripeConfig.plans) {
            window.stripeConfig.plans.basic.price = basic;
            window.stripeConfig.plans.premium.price = premium;
            window.stripeConfig.plans.free.price = 0;
        }
        // Update subscriptionPlans array prices
        if (typeof subscriptionPlans !== 'undefined' && Array.isArray(subscriptionPlans)) {
            subscriptionPlans.forEach(plan => {
                if (plan.id === 'basic') plan.price = basic;
                else if (plan.id === 'premium') plan.price = premium;
                else if (plan.id === 'free') plan.price = 0;
            });
        }
        // Update the provider registration dropdown to reflect new rates
        if (typeof updateProviderPlanDropdown === 'function') {
            updateProviderPlanDropdown();
        }
        const statusEl = document.getElementById('subscriptionRatesStatus');
        if (statusEl) {
            statusEl.textContent = '✅ Subscription rates saved to Firestore!';
            statusEl.style.color = '#27ae60';
        }
        showNotification('Subscription rates saved to Firestore!', 'success');
    } else {
        const statusEl = document.getElementById('subscriptionRatesStatus');
        if (statusEl) {
            statusEl.textContent = '❌ Failed to save rates. Check Firestore connection.';
            statusEl.style.color = '#e74c3c';
        }
        showNotification('Failed to save subscription rates.', 'error');
    }
}

// OVERRIDE app.js resetSubscriptionRates() - resets to defaults in Firestore (no localStorage)
async function resetSubscriptionRates() {
    if (!isAdminLoggedIn()) {
        showNotification('You must be logged in as Super Admin.', 'error');
        return;
    }
    if (!confirm('Are you sure you want to reset subscription rates to defaults?')) return;
    const settings = loadSiteSettings();
    const rates = { ...__DEFAULT_SUBSCRIPTION_RATES };
    const saved = await __saveSettingsToFirestore(settings, rates);
    if (saved) {
        __subscriptionRatesCache = { ...rates };
        const basicInput = document.getElementById('basicPlanRate');
        const premiumInput = document.getElementById('premiumPlanRate');
        if (basicInput) basicInput.value = rates.basic.toFixed(2);
        if (premiumInput) premiumInput.value = rates.premium.toFixed(2);
        // Update Stripe config
        if (window.stripeConfig && window.stripeConfig.plans) {
            window.stripeConfig.plans.basic.price = rates.basic;
            window.stripeConfig.plans.premium.price = rates.premium;
            window.stripeConfig.plans.free.price = 0;
        }
        if (typeof subscriptionPlans !== 'undefined' && Array.isArray(subscriptionPlans)) {
            subscriptionPlans.forEach(plan => {
                if (plan.id === 'basic') plan.price = rates.basic;
                else if (plan.id === 'premium') plan.price = rates.premium;
                else if (plan.id === 'free') plan.price = 0;
            });
        }
        // Update the provider registration dropdown to reflect reset rates
        if (typeof updateProviderPlanDropdown === 'function') {
            updateProviderPlanDropdown();
        }
        const statusEl = document.getElementById('subscriptionRatesStatus');
        if (statusEl) {
            statusEl.textContent = '✅ Subscription rates reset to defaults!';
            statusEl.style.color = '#27ae60';
        }
        showNotification('Subscription rates reset to defaults.', 'success');
    } else {
        showNotification('Failed to reset subscription rates.', 'error');
    }
}

// ==================== MONTHLY RENEWAL CONFIGURATION (SUPER ADMIN) ====================
// Default monthly renewal configuration for provider subscription plans.
// TRUE = plan renews every 30 days (recurring monthly billing).
// FALSE = plan renews on a custom/longer cycle.
const __DEFAULT_PLAN_RENEWAL_CONFIG = {
    free: true,
    basic: true,
    premium: true
};

let __planRenewalConfigCache = null;

// Load the monthly renewal config (from cache or Firestore)
function loadPlanRenewalConfig() {
    if (__planRenewalConfigCache) {
        return { ...__planRenewalConfigCache };
    }
    return { ...__DEFAULT_PLAN_RENEWAL_CONFIG };
}

// Load the renewal config from Firestore into the cache
async function __loadPlanRenewalConfigFromFirestore() {
    const db = window.firebaseDbInstance;
    if (!db) {
        console.warn('[Settings] Firestore not available, using default renewal config');
        __planRenewalConfigCache = { ...__DEFAULT_PLAN_RENEWAL_CONFIG };
        return __planRenewalConfigCache;
    }
    try {
        const doc = await db.collection('siteSettings').doc('main').get();
        if (doc.exists) {
            const data = doc.data() || {};
            __planRenewalConfigCache = {
                free: typeof data.freeRenewsMonthly === 'boolean' ? data.freeRenewsMonthly : __DEFAULT_PLAN_RENEWAL_CONFIG.free,
                basic: typeof data.basicRenewsMonthly === 'boolean' ? data.basicRenewsMonthly : __DEFAULT_PLAN_RENEWAL_CONFIG.basic,
                premium: typeof data.premiumRenewsMonthly === 'boolean' ? data.premiumRenewsMonthly : __DEFAULT_PLAN_RENEWAL_CONFIG.premium
            };
        } else {
            __planRenewalConfigCache = { ...__DEFAULT_PLAN_RENEWAL_CONFIG };
        }
    } catch (e) {
        console.warn('[Settings] Error loading renewal config from Firestore:', e.message);
        __planRenewalConfigCache = { ...__DEFAULT_PLAN_RENEWAL_CONFIG };
    }
    return __planRenewalConfigCache;
}

// Apply the renewal config to the SUBSCRIPTION_PLANS durationDays
function applyPlanRenewalConfigToPlans() {
    const renewalConfig = loadPlanRenewalConfig();
    // SUBSCRIPTION_PLANS is defined in app.js and exposed on window
    const plans = window.SUBSCRIPTION_PLANS;
    if (!plans) {
        console.warn('[Settings] SUBSCRIPTION_PLANS not available; skipping plan duration update');
        return;
    }
    Object.keys(plans).forEach(planId => {
        if (plans[planId]) {
            // Monthly renewal = 30 days; otherwise keep the plan's existing duration
            if (renewalConfig[planId] === true) {
                plans[planId].durationDays = 30;
                plans[planId].interval = 'month';
                plans[planId].renewsMonthly = true;
            } else if (typeof plans[planId].durationDays !== 'number') {
                // Fallback for plans without a duration
                plans[planId].durationDays = 30;
            }
        }
    });
    // Also update the subscriptionPlans array used by admin plan management
    if (typeof subscriptionPlans !== 'undefined' && Array.isArray(subscriptionPlans)) {
        subscriptionPlans.forEach(plan => {
            if (renewalConfig[plan.id] === true) {
                plan.durationDays = 30;
            }
        });
    }
}

// Save the monthly renewal configuration to Firestore
async function savePlanRenewalConfig() {
    const notify = window.showNotification || ((msg) => console.log('[Settings]', msg));
    if (!isAdminLoggedIn()) {
        notify('You must be logged in as Super Admin.', 'error');
        return;
    }

    const freeRenews = document.getElementById('freeRenewsMonthly');
    const basicRenews = document.getElementById('basicRenewsMonthly');
    const premiumRenews = document.getElementById('premiumRenewsMonthly');

    if (!freeRenews || !basicRenews || !premiumRenews) {
        notify('Renewal configuration inputs not found.', 'error');
        return;
    }

    const renewalConfig = {
        free: freeRenews.checked,
        basic: basicRenews.checked,
        premium: premiumRenews.checked
    };

    const db = window.firebaseDbInstance;
    if (!db) {
        notify('Firestore not available. Cannot save renewal configuration.', 'error');
        return;
    }

    try {
        await db.collection('siteSettings').doc('main').set({
            freeRenewsMonthly: renewalConfig.free,
            basicRenewsMonthly: renewalConfig.basic,
            premiumRenewsMonthly: renewalConfig.premium,
            updatedAt: new Date().toISOString()
        }, { merge: true });

        __planRenewalConfigCache = { ...renewalConfig };
        applyPlanRenewalConfigToPlans();

        const statusEl = document.getElementById('planRenewalConfigStatus');
        if (statusEl) {
            statusEl.textContent = '✅ Monthly renewal configuration saved to Firestore!';
            statusEl.style.color = '#27ae60';
        }
        notify('Monthly renewal configuration saved to Firestore!', 'success');
    } catch (e) {
        console.error('[Settings] Error saving renewal config to Firestore:', e.message);
        const statusEl = document.getElementById('planRenewalConfigStatus');
        if (statusEl) {
            statusEl.textContent = '❌ Failed to save renewal configuration. Check Firestore connection.';
            statusEl.style.color = '#e74c3c';
        }
        notify('Failed to save renewal configuration.', 'error');
    }
}

// Reset the monthly renewal configuration to defaults
async function resetPlanRenewalConfig() {
    const notify = window.showNotification || ((msg) => console.log('[Settings]', msg));
    if (!isAdminLoggedIn()) {
        notify('You must be logged in as Super Admin.', 'error');
        return;
    }
    if (!confirm('Are you sure you want to reset the monthly renewal configuration to defaults (all plans renew monthly)?')) return;

    const db = window.firebaseDbInstance;
    if (!db) {
        notify('Firestore not available. Cannot reset renewal configuration.', 'error');
        return;
    }

    try {
        await db.collection('siteSettings').doc('main').set({
            freeRenewsMonthly: __DEFAULT_PLAN_RENEWAL_CONFIG.free,
            basicRenewsMonthly: __DEFAULT_PLAN_RENEWAL_CONFIG.basic,
            premiumRenewsMonthly: __DEFAULT_PLAN_RENEWAL_CONFIG.premium,
            updatedAt: new Date().toISOString()
        }, { merge: true });

        __planRenewalConfigCache = { ...__DEFAULT_PLAN_RENEWAL_CONFIG };
        applyPlanRenewalConfigToPlans();

        // Reset the checkboxes in the UI
        const freeRenews = document.getElementById('freeRenewsMonthly');
        const basicRenews = document.getElementById('basicRenewsMonthly');
        const premiumRenews = document.getElementById('premiumRenewsMonthly');
        if (freeRenews) freeRenews.checked = __DEFAULT_PLAN_RENEWAL_CONFIG.free;
        if (basicRenews) basicRenews.checked = __DEFAULT_PLAN_RENEWAL_CONFIG.basic;
        if (premiumRenews) premiumRenews.checked = __DEFAULT_PLAN_RENEWAL_CONFIG.premium;

        const statusEl = document.getElementById('planRenewalConfigStatus');
        if (statusEl) {
            statusEl.textContent = '✅ Renewal configuration reset to defaults (all plans renew monthly)!';
            statusEl.style.color = '#27ae60';
        }
        notify('Monthly renewal configuration reset to defaults.', 'success');
    } catch (e) {
        console.error('[Settings] Error resetting renewal config in Firestore:', e.message);
        notify('Failed to reset renewal configuration.', 'error');
    }
}

// Make renewal config functions globally accessible
window.loadPlanRenewalConfig = loadPlanRenewalConfig;
window.savePlanRenewalConfig = savePlanRenewalConfig;
window.resetPlanRenewalConfig = resetPlanRenewalConfig;
window.applyPlanRenewalConfigToPlans = applyPlanRenewalConfigToPlans;

// Make country access restriction functions globally accessible
window.loadCountryAccessSettings = loadCountryAccessSettings;
window.saveCountryAccessSettings = saveCountryAccessSettings;
window.resetCountryAccessSettings = resetCountryAccessSettings;
window.toggleCountryAccessListVisibility = toggleCountryAccessListVisibility;

// ==================== REAL-TIME SETTINGS SYNC ====================
// Sets up an onSnapshot listener on the siteSettings/main document so that
// footer text and subscription rates stay in sync across all browsers and
// auto-refresh whenever an admin saves new values - no page refresh needed.
// This also fixes the "settings revert on refresh" issue because the cache
// is always updated from Firestore the moment data changes.
let __siteSettingsUnsubscribe = null;

function __setupSiteSettingsListener() {
    if (__siteSettingsReadBlocked) {
        return;
    }
    const db = window.firebaseDbInstance;
    if (!db) {
        // Firestore not initialized yet (e.g. first page load before the
        // Firebase SDK bootstrap completes). Retry shortly - this ensures
        // the footer reflects saved settings on the very first load, not
        // just after admin login.
        console.log('[Settings] Firestore not ready yet, retrying listener setup in 500ms');
        setTimeout(function() {
            if (window.firebaseDbInstance) {
                __setupSiteSettingsListener();
            } else {
                // Keep retrying until Firestore is available (max safety)
                console.warn('[Settings] Firestore still not ready after retry');
            }
        }, 500);
        return;
    }

    // Clear any existing listener to avoid duplicates
    if (typeof __siteSettingsUnsubscribe === 'function') {
        try { __siteSettingsUnsubscribe(); } catch (e) {}
        __siteSettingsUnsubscribe = null;
    }

    try {
        __siteSettingsUnsubscribe = db.collection('siteSettings').doc('main')
            .onSnapshot(function(doc) {
                if (doc && doc.exists) {
                    const data = doc.data() || {};
                    // Update in-memory caches from Firestore
                    __siteSettingsCache = {
                        footerText: data.footerText || __DEFAULT_SITE_SETTINGS.footerText
                    };
                    __subscriptionRatesCache = {
                        free: 0.00,
                        basic: (typeof data.basicRate === 'number') ? data.basicRate : __DEFAULT_SUBSCRIPTION_RATES.basic,
                        premium: (typeof data.premiumRate === 'number') ? data.premiumRate : __DEFAULT_SUBSCRIPTION_RATES.premium
                    };
                    window.__countryAccessSettingsCache = {
                        mode: data.countryAccessMode || __DEFAULT_COUNTRY_ACCESS_SETTINGS.mode,
                        countries: Array.isArray(data.countryAccessList) ? data.countryAccessList : __DEFAULT_COUNTRY_ACCESS_SETTINGS.countries,
                        reason: typeof data.countryAccessReason === 'string' ? data.countryAccessReason : __DEFAULT_COUNTRY_ACCESS_SETTINGS.reason
                    };
                    console.log('[Settings] Real-time settings update received from Firestore');

                    // Re-render footer if it exists
                    if (typeof renderFooter === 'function') renderFooter();

                    // Update the provider registration dropdown to reflect new rates
                    if (typeof updateProviderPlanDropdown === 'function') {
                        updateProviderPlanDropdown();
                    }

                    // Re-render settings panel if it's currently visible
                    const settingsPanel = document.getElementById('admin-settings-panel');
                    if (settingsPanel && settingsPanel.classList.contains('active')) {
                        // Only re-render the input values without wiping the DOM
                        const footerTextInput = document.getElementById('footerTextInput');
                        if (footerTextInput) footerTextInput.value = __siteSettingsCache.footerText;
                        const footerPreview = document.getElementById('footerPreview');
                        if (footerPreview) footerPreview.textContent = __siteSettingsCache.footerText;
                        const basicInput = document.getElementById('basicPlanRate');
                        if (basicInput) basicInput.value = __subscriptionRatesCache.basic.toFixed(2);
                        const premiumInput = document.getElementById('premiumPlanRate');
                        if (premiumInput) premiumInput.value = __subscriptionRatesCache.premium.toFixed(2);
                        const modeEl = document.getElementById('countryAccessMode');
                        if (modeEl) modeEl.value = window.__countryAccessSettingsCache.mode;
                        const listEl = document.getElementById('countryAccessList');
                        if (listEl) listEl.value = (window.__countryAccessSettingsCache.countries || []).join(', ');
                        const reasonEl = document.getElementById('countryAccessReason');
                        if (reasonEl) reasonEl.value = window.__countryAccessSettingsCache.reason || '';
                        if (typeof toggleCountryAccessListVisibility === 'function') toggleCountryAccessListVisibility();
                    }
                } else {
                    console.log('[Settings] siteSettings/main does not exist (or listener error), using defaults');
                    __siteSettingsCache = { ...__DEFAULT_SITE_SETTINGS };
                    __subscriptionRatesCache = { ...__DEFAULT_SUBSCRIPTION_RATES };
                    window.__countryAccessSettingsCache = { ...__DEFAULT_COUNTRY_ACCESS_SETTINGS, countries: [] };
                    if (typeof renderFooter === 'function') renderFooter();
                }
            }, function(err) {
                // Permission errors (guest not logged in) are expected - keep defaults
                console.warn('[Settings] Real-time settings listener error (this is OK if not logged in):', err.message);
                if (String(err && (err.code || err.message || err)).toLowerCase().includes('permission')) {
                    __siteSettingsReadBlocked = true;
                    if (typeof __siteSettingsUnsubscribe === 'function') {
                        try { __siteSettingsUnsubscribe(); } catch (e) {}
                        __siteSettingsUnsubscribe = null;
                    }
                    console.warn('[Settings] Disabling siteSettings listener after permission denial until next page reload.');
                }
                __siteSettingsCache = { ...__DEFAULT_SITE_SETTINGS };
                __subscriptionRatesCache = { ...__DEFAULT_SUBSCRIPTION_RATES };
                window.__countryAccessSettingsCache = window.__countryAccessSettingsCache || { ...__DEFAULT_COUNTRY_ACCESS_SETTINGS, countries: [] };
                if (typeof renderFooter === 'function') renderFooter();
            });
    } catch (e) {
        console.warn('[Settings] Could not set up real-time settings listener:', e.message);
    }
}

// Load settings from Firestore on page load so the footer reflects saved text.
// CRITICAL FIX: Wait for Firestore to be fully initialized (via
// ensureFirebaseReady) BEFORE reading siteSettings. Previously the read ran
// immediately on DOMContentLoaded when window.firebaseDbInstance was still
// null, so __loadSiteSettingsFromFirestore() returned defaults ("© 2026") and
// the footer rendered with those defaults on first load. Now we wait for the
// SDK bootstrap, then load + render the real saved settings.
//
// ADDITIONAL FIX: Because app.js's renderFooter() may run FIRST with the
// defaults during its own DOMContentLoaded handler, we also poll repeatedly
// and actively RE-FETCH the settings from Firestore until the footer shows
// the saved value. This guarantees the footer shows the saved "© 2026" text
// even if the SDK bootstrap / network is slow, regardless of script load order.
async function initAdminSubscriptionsDOM() {
    // Wait for Firestore to be ready (the modular bootstrap sets
    // window.firebaseDbInstance). This is the key fix for first-load sync.
    try {
        if (typeof window.ensureFirebaseReady === 'function') {
            await window.ensureFirebaseReady();
        }
    } catch (e) {
        console.warn('[Settings] ensureFirebaseReady failed, continuing:', e.message);
    }
    // Now Firestore is ready (or we gave up after timeout) - load settings.
    await __loadSiteSettingsFromFirestore();
    if (typeof renderFooter === 'function') renderFooter();
    await __loadSubscriptionRatesFromFirestore();
    // Update the provider registration dropdown with the loaded rates
    if (typeof updateProviderPlanDropdown === 'function') {
        updateProviderPlanDropdown();
    }
    // Set up the real-time listener now that Firestore is ready.
    __setupSiteSettingsListener();

    // Poll: actively re-fetch settings from Firestore and re-render the footer
    // until the saved value is reflected. This overcomes any race where the
    // initial read returned defaults because Firestore wasn't ready yet or the
    // rules hadn't propagated. We re-fetch every 800ms for up to ~16 seconds.
    let pollAttempts = 0;
    const pollTimer = setInterval(async function() {
        if (__siteSettingsReadBlocked) {
            clearInterval(pollTimer);
            return;
        }
        pollAttempts++;
        // Re-fetch from Firestore to get the latest saved footer text
        await __loadSiteSettingsFromFirestore();
        // Once we have a cache and it contains a real saved value (not the
        // default "© 2026"), re-render the footer with it.
        if (__siteSettingsCache && __siteSettingsCache.footerText &&
            __siteSettingsCache.footerText !== __DEFAULT_SITE_SETTINGS.footerText) {
            if (typeof renderFooter === 'function') renderFooter();
            clearInterval(pollTimer);
            console.log('[Settings] Footer re-rendered with saved settings after ' + pollAttempts + ' poll attempts');
        } else if (pollAttempts > 20) {
            // Give up after ~16s (20 * 800ms) to avoid infinite polling
            clearInterval(pollTimer);
        }
    }, 800);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAdminSubscriptionsDOM);
} else {
    initAdminSubscriptionsDOM();
}

// Expose for use after admin login (in app.js)
window.__setupSiteSettingsListener = __setupSiteSettingsListener;

// Make Firestore-backed functions globally accessible
window.saveFooterText = saveFooterText;
window.resetFooterText = resetFooterText;
window.saveSubscriptionRates = saveSubscriptionRates;
window.resetSubscriptionRates = resetSubscriptionRates;
window.loadSiteSettings = loadSiteSettings;
window.loadSubscriptionRates = loadSubscriptionRates;

// ==================== SUPER ADMIN TAB SWITCHING ====================
// Switch admin dashboard tabs
function switchAdminTab(tabName) {
    console.log('[Admin] switchAdminTab called:', tabName);

    // Get all tab buttons
    const tabs = document.querySelectorAll('.admin-panel-tab');

    // Update active tab button
    tabs.forEach(t => {
        t.classList.remove('active');
        if (t.dataset.adminPanel === tabName) {
            t.classList.add('active');
        }
    });

    // Hide all admin panel sections
    const allSections = document.querySelectorAll('.admin-panel-section');
    allSections.forEach(section => {
        section.classList.remove('active');
        section.style.display = 'none';
    });

    // Show target section
    const targetSection = document.getElementById(`admin-${tabName}-panel`);
    if (targetSection) {
        targetSection.classList.add('active');
        targetSection.style.display = '';
    }

    // Render data for specific panel
    if (tabName === 'stats') {
        renderAdminStats();
    } else if (tabName === 'subscriptions') {
        renderAdminSubscriptions();
    } else if (tabName === 'clients') {
        renderAdminClients();
    } else if (tabName === 'providers') {
        renderAdminProviders();
    } else if (tabName === 'orders') {
        renderAdminOrders();
    } else if (tabName === 'reviews') {
        renderAdminReviews();
    } else if (tabName === 'settings') {
        renderAdminSettings();
    }

    console.log('[Admin] Switched to tab:', tabName);
}

// Make functions global
window.initClientDashboardTabs = initClientDashboardTabs;
window.showClientDashboardTab = showClientDashboardTab;
window.switchAdminTab = switchAdminTab;

// ==================== SUBSCRIPTION MANAGEMENT GLOBAL FUNCTIONS ====================
// These are called from onclick attributes in the admin panel HTML
// The functions in app.js are not exposed as globals, so we create safe wrappers here.

// Approve a provider (set subscriptionStatus to 'approved' via backend Cloud Function)
window.approveProvider = async function(providerId) {
    if (!isAdminLoggedIn()) {
        showNotification('You must be logged in as Super Admin.', 'error');
        return;
    }
    if (!confirm('Are you sure you want to approve this provider?')) return;

    try {
        const provider = providers.find(p => p.id === providerId || p.uid === providerId);
        if (typeof window.callProtectedFunction === 'function') {
            await window.callProtectedFunction('approveProvider', { providerId: String(providerId) });
        } else if (window.firebaseDbInstance) {
            await window.firebaseDbInstance.collection('providers').doc(providerId).set({
                subscriptionStatus: 'approved',
                accountStatus: 'active',
                verificationStatus: 'verified',
                subscriptionApprovedAt: new Date().toISOString()
            }, { merge: true });
        }
        if (provider) {
            provider.subscriptionStatus = 'approved';
            provider.accountStatus = 'active';
            provider.verificationStatus = 'verified';
            provider.subscriptionApprovedAt = new Date().toISOString();
        }

        showNotification(`${(provider && provider.name) || 'Provider'} has been approved!`, 'success');
        renderAdminSubscriptions();
        if (typeof renderAdminProviders === 'function') renderAdminProviders();
    } catch (e) {
        console.error('[Admin] Approve failed:', e);
        showNotification('Failed to approve provider: ' + (e.message || String(e)), 'error');
    }
};

// Reject a provider
window.rejectProvider = async function(providerId) {
    if (!isAdminLoggedIn()) return;
    const reason = prompt('Enter rejection reason:');
    if (reason === null) return;

    try {
        const provider = providers.find(p => p.id === providerId || p.uid === providerId);
        if (typeof window.callProtectedFunction === 'function') {
            await window.callProtectedFunction('rejectProvider', { providerId: String(providerId), reason: String(reason) });
        } else if (window.firebaseDbInstance) {
            await window.firebaseDbInstance.collection('providers').doc(providerId).set({
                subscriptionStatus: 'rejected',
                accountStatus: 'rejected',
                verificationStatus: 'rejected',
                subscriptionRejectionReason: reason
            }, { merge: true });
        }
        if (provider) {
            provider.subscriptionStatus = 'rejected';
            provider.accountStatus = 'rejected';
            provider.verificationStatus = 'rejected';
            provider.subscriptionRejectionReason = reason;
        }

        showNotification(`${(provider && provider.name) || 'Provider'} has been rejected.`, 'success');
        renderAdminSubscriptions();
        if (typeof renderAdminProviders === 'function') renderAdminProviders();
    } catch (e) {
        console.error('[Admin] Reject failed:', e);
        showNotification('Failed to reject provider: ' + (e.message || String(e)), 'error');
    }
};

// Suspend a provider
window.suspendProvider = async function(providerId) {
    if (!isAdminLoggedIn()) return;
    const reason = prompt('Enter suspension reason:');
    if (reason === null) return;

    try {
        const provider = providers.find(p => p.id === providerId || p.uid === providerId);
        if (typeof window.callProtectedFunction === 'function') {
            await window.callProtectedFunction('changeProviderSubscription', { providerId: String(providerId), action: 'suspend', reason: String(reason) });
        } else if (window.firebaseDbInstance) {
            await window.firebaseDbInstance.collection('providers').doc(providerId).set({
                subscriptionStatus: 'suspended',
                subscriptionSuspendedReason: reason
            }, { merge: true });
        }
        if (provider) {
            provider.subscriptionStatus = 'suspended';
            provider.subscriptionSuspendedReason = reason;
        }

        showNotification(`${(provider && provider.name) || 'Provider'} has been suspended.`, 'success');
        renderAdminSubscriptions();
        if (typeof renderAdminProviders === 'function') renderAdminProviders();
    } catch (e) {
        console.error('[Admin] Suspend failed:', e);
        showNotification('Failed to suspend provider: ' + (e.message || String(e)), 'error');
    }
};

// Activate a suspended provider
window.activateProvider = async function(providerId) {
    if (!isAdminLoggedIn()) return;

    try {
        const provider = providers.find(p => p.id === providerId || p.uid === providerId);
        const plan = (provider && provider.subscriptionPlan) || 'free';
        if (typeof window.callProtectedFunction === 'function') {
            await window.callProtectedFunction('changeProviderSubscription', { providerId: String(providerId), action: 'activate', subscriptionPlan: plan });
        } else if (window.firebaseDbInstance) {
            await window.firebaseDbInstance.collection('providers').doc(providerId).set({
                subscriptionStatus: 'approved',
                subscriptionSuspendedReason: null
            }, { merge: true });
        }
        if (provider) {
            provider.subscriptionStatus = 'approved';
            delete provider.subscriptionSuspendedReason;
        }

        showNotification(`${(provider && provider.name) || 'Provider'} has been reactivated!`, 'success');
        renderAdminSubscriptions();
        if (typeof renderAdminProviders === 'function') renderAdminProviders();
    } catch (e) {
        console.error('[Admin] Activate failed:', e);
        showNotification('Failed to reactivate provider: ' + (e.message || String(e)), 'error');
    }
};

// Renew a provider's subscription
window.renewProvider = async function(providerId) {
    if (!isAdminLoggedIn()) return;

    try {
        const provider = providers.find(p => p.id === providerId || p.uid === providerId);
        const plan = (provider && provider.subscriptionPlan) || 'free';
        const plans = window.SUBSCRIPTION_PLANS || {};
        const durationDays = plans[plan]?.durationDays || 30;
        const startDate = new Date();
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + durationDays);

        if (typeof window.callProtectedFunction === 'function') {
            await window.callProtectedFunction('changeProviderSubscription', {
                providerId: String(providerId),
                action: 'renew',
                subscriptionPlan: plan,
                subscriptionStart: startDate.toISOString(),
                subscriptionEnd: endDate.toISOString()
            });
        } else if (window.firebaseDbInstance) {
            await window.firebaseDbInstance.collection('providers').doc(providerId).set({
                subscriptionStatus: 'approved',
                subscriptionStart: startDate.toISOString(),
                subscriptionEnd: endDate.toISOString()
            }, { merge: true });
        }
        if (provider) {
            provider.subscriptionStatus = 'approved';
            provider.subscriptionStart = startDate.toISOString();
            provider.subscriptionEnd = endDate.toISOString();
        }

        showNotification(`${(provider && provider.name) || 'Provider'}'s subscription renewed until ${endDate.toLocaleDateString()}!`, 'success');
        renderAdminSubscriptions();
        if (typeof renderAdminProviders === 'function') renderAdminProviders();
    } catch (e) {
        console.error('[Admin] Renew failed:', e);
        showNotification('Failed to renew subscription: ' + (e.message || String(e)), 'error');
    }
};

async function deleteAdminAccountThroughBackend(kind, identifier) {
    if (!isAdminLoggedIn()) {
        showNotification('Admin login is required. Nothing was deleted.', 'error');
        return { deleted: false };
    }

    const normalizedIdentifier = String(identifier || '').trim();
    if (!normalizedIdentifier) {
        showNotification(`Missing ${kind} identifier. Nothing was deleted.`, 'error');
        return { deleted: false };
    }

    const backendDelete = kind === 'client'
        ? window.adminBackendDeleteClient
        : window.adminBackendDeleteProvider;
    if (typeof backendDelete !== 'function') {
        showNotification(`Secure ${kind} deletion is unavailable. Nothing was deleted.`, 'error');
        return { deleted: false };
    }

    try {
        const result = await backendDelete({ uidOrIdOrEmail: normalizedIdentifier });
        if (!result || result.deleted !== true || (result.authDeleted !== true && result.authAlreadyAbsent !== true)) {
            throw new Error(`The server did not confirm complete ${kind} account deletion.`);
        }

        await bootstrapAdminData({ force: true });
        if (typeof renderAdminSubscriptions === 'function') renderAdminSubscriptions();
        if (kind === 'client' && typeof renderAdminClients === 'function') renderAdminClients();
        if (kind === 'provider' && typeof renderAdminProviders === 'function') renderAdminProviders();
        if (typeof renderAdminStats === 'function') renderAdminStats();
        showNotification(`${kind === 'client' ? 'Client' : 'Provider'} login account and all attached data were deleted successfully.`, 'success');
        return result;
    } catch (error) {
        console.error(`[Admin] Complete ${kind} deletion failed:`, error);
        showNotification(`${kind === 'client' ? 'Client' : 'Provider'} was not completely deleted: ${error.message || 'server error'}`, 'error');
        return { deleted: false, error: error.message || 'server error' };
    }
}

window.deleteAdminProvider = async function(providerId) {
    if (!confirm('Are you sure you want to permanently delete this provider, their login account, and all attached data?')) {
        return { deleted: false };
    }
    return deleteAdminAccountThroughBackend('provider', providerId);
};

window.deleteAdminClient = async function(clientIdOrEmail) {
    if (!confirm('Are you sure you want to permanently delete this client, their login account, and all attached data?')) {
        return { deleted: false };
    }
    return deleteAdminAccountThroughBackend('client', clientIdOrEmail);
};

// ==================== SUPER ADMIN PANEL ====================
// Initialize Admin Panel - FIXED VERSION
function initAdminPanel() {
    console.log('[Admin] Initializing admin panel...');

    // Find the admin login form
    const adminLogForm = document.getElementById('adminLogForm');
    if (!adminLogForm) {
        console.log('[Admin] adminLogForm not found!');
        // Try alternative form ID
        const altForm = document.getElementById('adminLoginForm');
        if (altForm) {
            console.log('[Admin] Found alt admin login form');
            handleAltAdminLogin(altForm);
            return;
        }
        return;
    }

    // Delegate to the Firebase-based performAdminLogin() (which is the same as the HTML onclick handler).
    // This ensures both form submit (Enter key) and button click trigger the secure Firebase auth flow.
    adminLogForm.addEventListener('submit', function(e) {
        e.preventDefault();
        e.stopPropagation();
        console.log('[Admin] Form submit intercepted, delegating to performAdminLogin');
        if (typeof window.performAdminLogin === 'function') {
            window.performAdminLogin();
        } else {
            showNotification('Admin login function not loaded yet. Please try again.', 'error');
        }
        return false;
    });

    // Also handle button click directly
    const loginBtn = adminLogForm.querySelector('button[type="submit"]');
    if (loginBtn) {
        loginBtn.addEventListener('click', function(e) {
            e.preventDefault();
            e.stopPropagation();
            console.log('[Admin] Button click intercepted, delegating to performAdminLogin');
            if (typeof window.performAdminLogin === 'function') {
                window.performAdminLogin();
            } else {
                showNotification('Admin login function not loaded yet. Please try again.', 'error');
            }
            return false;
        });
    }

    console.log('[Admin] Admin panel initialized');

    // Admin logout button
    const adminLogoutBtn = document.getElementById('adminLogoutBtn');
    if (adminLogoutBtn) {
        adminLogoutBtn.onclick = function(e) {
            if (e) {
                e.preventDefault();
            }

            // Single source of truth:
            // Use the already-defined global handler below so logout is consistent.
            if (typeof window.handleAdminLogout === 'function') {
                window.handleAdminLogout();
            } else {
                console.warn('[Admin] window.handleAdminLogout missing; falling back to local clear');
                try { currentAdmin = null; } catch {}
                try { clearProviderAndClientSessions && clearProviderAndClientSessions(); } catch {}
                try { hideProviderAndClientDashboards && hideProviderAndClientDashboards(); } catch {}
                try { updateHeaderUserBadge && updateHeaderUserBadge(); } catch {}

                const adminDashboard = document.getElementById('adminDashboard');
                const adminLoginSection = document.getElementById('adminLoginSection');
                if (adminDashboard) adminDashboard.classList.add('hidden');
                if (adminLoginSection) adminLoginSection.classList.remove('hidden');
            }
        };
    }

    // Admin panel tabs
    const adminPanelTabs = document.querySelectorAll('.admin-panel-tab');
    if (adminPanelTabs && adminPanelTabs.length > 0) {
        adminPanelTabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const targetPanel = tab.dataset.adminPanel;
                console.log('[Admin] Tab clicked:', targetPanel);

                // Update active tab
                adminPanelTabs.forEach(t => t.classList.remove('active'));
                tab.classList.add('active');

                // Hide all admin panel sections
                const allSections = document.querySelectorAll('.admin-panel-section');
                allSections.forEach(section => section.classList.remove('active'));

                // Show target section
                const targetSection = document.getElementById(`admin-${targetPanel}-panel`);
                if (targetSection) {
                    targetSection.classList.add('active');
                }

                // Render data for specific panel
                if (targetPanel === 'stats') {
                    renderAdminStats();
                } else if (targetPanel === 'subscriptions') {
                    renderAdminSubscriptions();
                } else if (targetPanel === 'clients') {
                    renderAdminClients();
                } else if (targetPanel === 'providers') {
                    renderAdminProviders();
                } else if (targetPanel === 'orders') {
                    renderAdminOrders();
                } else if (targetPanel === 'reviews') {
                    renderAdminReviews();
                } else if (targetPanel === 'settings') {
                    renderAdminSettings();
                }
            });
        });
    }
}

// Handle Admin Logout - FIXED: Make global so onclick in HTML works
function handleAdminLogout() {
    console.log('[Admin] Logging out...');

    // Clear admin session
    currentAdmin = null;

    // Clear any cached role sessions
    if (typeof clearProviderAndClientSessions === 'function') clearProviderAndClientSessions();

    // Hide provider/client dashboards to avoid stale UI
    if (typeof hideProviderAndClientDashboards === 'function') hideProviderAndClientDashboards();

    // Attempt to sign out of Firebase Auth too (fixes "logout not working" for superadmin)
    try {
        if (typeof window.firebaseSignOut === 'function') {
            window.firebaseSignOut().catch(() => {});
        } else if (typeof window.firebaseSignOutLegacy === 'function') {
            window.firebaseSignOutLegacy().catch(() => {});
        }
    } catch (e) {
        console.warn('[Admin] firebaseSignOut failed:', e);
    }

    // Hide dashboard, show login (with null safety checks)
    const adminDashboard = document.getElementById('adminDashboard');
    const adminLoginSection = document.getElementById('adminLoginSection');

    if (adminDashboard) adminDashboard.classList.add('hidden');
    if (adminLoginSection) adminLoginSection.classList.remove('hidden');

    // Update header badge after logout to show correct status
    if (typeof updateHeaderUserBadge === 'function') updateHeaderUserBadge();

    console.log('[Admin] Logged out successfully');
    showNotification('Logged out successfully!', 'success');
}

// Make handleAdminLogout globally accessible for onclick in HTML
window.handleAdminLogout = handleAdminLogout;

// Render Admin Statistics
async function renderAdminStats() {
    const adminGuard = typeof isAdminLoggedIn === 'function' ? isAdminLoggedIn : fallbackIsAdminLoggedIn;
    if (!adminGuard || !adminGuard()) return;

    const statsContainer = document.getElementById('adminStats') || document.getElementById('admin-stats-panel');
    if (!statsContainer) return;

    let bootstrapResult = null;
    try {
        bootstrapResult = await bootstrapAdminData();
    } catch (e) {
        console.warn('[Admin] renderAdminStats bootstrap failed, using in-memory data:', e && e.message ? e.message : e);
        bootstrapResult = { ok: false, error: e && e.message ? e.message : String(e) };
    }

    const bootstrapError = (typeof window !== 'undefined' && window.__adminBootstrapError) ? window.__adminBootstrapError : null;
    if ((bootstrapResult && bootstrapResult.ok === false && bootstrapResult.error) || bootstrapError) {
        const message = bootstrapError && bootstrapError.message
            ? bootstrapError.message
            : (bootstrapResult && bootstrapResult.error ? bootstrapResult.error : 'Admin dashboard data failed to load.');
        const recoveryHint = getAdminBootstrapRecoveryHint(bootstrapResult || bootstrapError);
        statsContainer.innerHTML = `
            <div class="empty-state" style="border:1px solid #d93025;background:#fff3f3;color:#8a1c1c;padding:14px;border-radius:8px;">
                <p><strong>Admin data load failed.</strong></p>
                <p>${safeEscapeHtml(message)}</p>
                <p>${safeEscapeHtml(recoveryHint)}</p>
            </div>
        `;
        return;
    }

    const statsClients = Array.isArray(window.clients) ? window.clients : (Array.isArray(clients) ? clients : []);
    const statsProviders = Array.isArray(window.providers) ? window.providers : (Array.isArray(providers) ? providers : []);
    const statsMenuItems = Array.isArray(window.menuItems) ? window.menuItems : (Array.isArray(menuItems) ? menuItems : []);
    const statsOrders = Array.isArray(window.orders) ? window.orders : (Array.isArray(orders) ? orders : (Array.isArray(window.clientMessages) ? window.clientMessages : (Array.isArray(clientMessages) ? clientMessages : [])));
    const statsReviews = Array.isArray(window.reviews) ? window.reviews : (Array.isArray(reviews) ? reviews : []);

    const totalClients = statsClients.length;
    const nowTs = Date.now();
    const totalProviders = statsProviders.filter((p) => {
        const status = String(p && p.subscriptionStatus ? p.subscriptionStatus : 'pending').toLowerCase();
        if (status !== 'approved') return false;
        if (!p || !p.subscriptionEnd) return true;
        const endTs = new Date(p.subscriptionEnd).getTime();
        if (Number.isNaN(endTs)) return true;
        return endTs >= nowTs;
    }).length;
    const totalMenuItems = statsMenuItems.length;
    const totalOrders = statsOrders.length;
    const totalReviews = statsReviews.length;

    const completedOrders = statsOrders.filter((order) => {
        const status = String(order && order.status ? order.status : 'pending').toLowerCase();
        return status === 'completed';
    }).length;

    const pendingOrders = statsOrders.filter((order) => {
        const status = String(order && order.status ? order.status : 'pending').toLowerCase();
        return status === 'pending' || status === 'queued' || status === 'new';
    }).length;

    const totalRevenue = statsOrders
        .filter((order) => {
            const status = String(order && order.status ? order.status : 'pending').toLowerCase();
            return status === 'completed';
        })
        .reduce((sum, order) => sum + (Number(order && order.price) || 0), 0);

    const activeProviders = new Set(statsMenuItems.map((m) => m && (m.providerId || m.provider_id)).filter(Boolean)).size;

    statsContainer.innerHTML = `
        <div class="stat-card">
            <div class="stat-value">${totalClients}</div>
            <div class="stat-label">Total Clients</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${totalProviders}</div>
            <div class="stat-label">Total Restaurants/Caterers</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${activeProviders}</div>
            <div class="stat-label">Active Providers</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${totalMenuItems}</div>
            <div class="stat-label">Menu Items</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${totalOrders}</div>
            <div class="stat-label">Total Orders</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${pendingOrders}</div>
            <div class="stat-label">Pending Orders</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${completedOrders}</div>
            <div class="stat-label">Completed Orders</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">$${totalRevenue.toFixed(2)}</div>
            <div class="stat-label">Total Revenue</div>
        </div>
        <div class="stat-card">
            <div class="stat-value">${totalReviews}</div>
            <div class="stat-label">Total Reviews</div>
        </div>
    `;
}

// Legacy stub superseded by async renderAdminClients above
function renderAdminClientsLegacyStub() {
    if (!isAdminLoggedIn()) return;
    const clientsList = document.getElementById('adminClientsList');
    if (!clientsList) return;

    if (clients.length === 0) {
        clientsList.innerHTML = '<div class="empty-state"><p>No clients registered yet.</p></div>';
        return;
    }

    // Sort by newest first
    const sortedClients = [...clients].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    clientsList.innerHTML = sortedClients.map(client => {
        const createdDate = client.createdAt ? formatDateTime(client.createdAt) : 'N/A';
        const ordersCount = clientMessages.filter(m => m.clientEmail === client.email).length;

        return `
            <div class="admin-item">
                <div class="admin-item-header">
                    <span class="admin-item-name">${escapeHtml(client.name)}</span>
                    <span class="admin-item-date">Registered: ${createdDate}</span>
                </div>
                <div class="admin-item-details">
                    <span>📧 ${escapeHtml(client.email)}</span>
                    <span>📞 ${escapeHtml(client.phone || 'N/A')}</span>
                    <span>🌍 ${escapeHtml(client.country || 'N/A')}</span>
                    <span>📦 ${ordersCount} orders</span>
                </div>
                <div class="admin-item-actions">
                    <button class="delete-btn" onclick="deleteAdminClient('${client.id}')">Delete</button>
                </div>
            </div>
        `;
    }).join('');
}

// Legacy stub superseded by async renderAdminProviders above
function renderAdminProvidersLegacyStub() {
    if (!isAdminLoggedIn()) return;
    const providersList = document.getElementById('adminProvidersList');
    if (!providersList) return;

    if (providers.length === 0) {
        providersList.innerHTML = '<div class="empty-state"><p>No restaurants/caterers registered yet.</p></div>';
        return;
    }

    // Sort by newest first
    const sortedProviders = [...providers].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    providersList.innerHTML = sortedProviders.map(provider => {
        const createdDate = provider.createdAt ? formatDateTime(provider.createdAt) : 'N/A';
        const menuCount = menuItems.filter(m => m.providerId === provider.id).length;
        const ordersCount = clientMessages.filter(m => m.providerId === provider.id).length;

        // Get subscription status badge
        const subStatus = provider.subscriptionStatus || 'pending';
        let statusBadge = '';
        if (subStatus === 'pending') {
            statusBadge = '<span class="sub-status-badge pending">⏳ Pending</span>';
        } else if (subStatus === 'approved') {
            statusBadge = '<span class="sub-status-badge approved">✅ Active</span>';
        } else if (subStatus === 'rejected') {
            statusBadge = '<span class="sub-status-badge rejected">❌ Rejected</span>';
        } else if (subStatus === 'suspended') {
            statusBadge = '<span class="sub-status-badge suspended">⛔ Suspended</span>';
        }

        return `
            <div class="admin-item">
                <div class="admin-item-header">
                    <span class="admin-item-name">${escapeHtml(provider.name)}</span>
                    <span class="admin-item-type">${escapeHtml(provider.type)}</span>
                    ${statusBadge}
                </div>
                <div class="admin-item-details">
                    <span>📧 ${escapeHtml(provider.email)}</span>
                    <span>📞 ${escapeHtml(provider.phone)}</span>
                    <span>📍 ${escapeHtml(provider.address || 'N/A')}</span>
                    <span>🌍 ${escapeHtml(provider.country)}</span>
                </div>
                <div class="admin-item-stats">
                    <span>🍽️ ${menuCount} menu items</span>
                    <span>📦 ${ordersCount} orders</span>
                    <span>Joined: ${createdDate}</span>
                </div>
                <div class="admin-item-actions">
                    <button class="delete-btn" onclick="deleteAdminProvider('${provider.id}')">Delete</button>
                </div>
            </div>
        `;
    }).join('');
}

// ==================== SUBSCRIPTION PLANS CONFIGURATION (SUPER ADMIN) ====================
// Load subscription plans from localStorage or use defaults
// NOTE: subscriptionPlans is already declared in app.js in the global scope.
// This file only loads/writes the plans; it does NOT redeclare the variable.
// The plans are initialized in app.js and shared via the global scope.
// NOTE: subscriptionPlans is declared with `let` in app.js (global scope).
// We must NOT use `var` here because `var` is hoisted to the global scope
// even inside an `if` block, causing a SyntaxError due to redeclaration
// with the existing `let` declaration in app.js.
// Instead, we just assign to the existing global variable if app.js
// hasn't loaded yet (defensive fallback). No `var`/`let`/`const` keyword
// means we only assign to (or create) the global property, avoiding the
// lexical redeclaration conflict.
if (typeof window.subscriptionPlans === 'undefined') {
    // Only initialize if app.js hasn't loaded yet (defensive fallback)
    window.subscriptionPlans = [
        { id: 'free', name: 'Free', price: 0, durationDays: 30, maxItems: 10, features: ['basic_menu', 'limited_orders'], isActive: true },
        { id: 'basic', name: 'Basic', price: __DEFAULT_SUBSCRIPTION_RATES.basic, durationDays: 30, maxItems: 50, features: ['full_menu', 'unlimited_orders', 'analytics'], isActive: true },
        { id: 'premium', name: 'Premium', price: __DEFAULT_SUBSCRIPTION_RATES.premium, durationDays: 30, maxItems: 999999, features: ['full_menu', 'unlimited_orders', 'analytics', 'priority_support', 'custom_branding'], isActive: true }
    ];
}

// Save subscription plans through the main app implementation so changes persist to Firestore.
// Capture the original handler once up front so the wrapper never rebinds itself and
// recursively calls its own replacement.
const appSaveSubscriptionPlans = (typeof window.__wtE_baseSaveSubscriptionPlans === 'function')
    ? window.__wtE_baseSaveSubscriptionPlans
    : ((typeof window.saveSubscriptionPlans === 'function' && window.saveSubscriptionPlans !== saveSubscriptionPlans)
        ? window.saveSubscriptionPlans
        : null);

function saveSubscriptionPlans() {
    const baseHandler = appSaveSubscriptionPlans;

    if (typeof baseHandler === 'function' && baseHandler !== saveSubscriptionPlans) {
        return baseHandler();
    }

    if (typeof window.subscriptionPlans !== 'undefined' && Array.isArray(window.subscriptionPlans)) {
        if (typeof window.__subscriptionPlansCache === 'undefined') {
            window.__subscriptionPlansCache = window.subscriptionPlans.map(plan => ({ ...plan }));
        }
    }

    return null;
}
window.__wtE_baseSaveSubscriptionPlans = appSaveSubscriptionPlans;
window.saveSubscriptionPlans = saveSubscriptionPlans;

// Show Add/Edit Subscription Plan Form
